import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { SegP } from "../../../SegUI/SegP/SegP";
import { SegInput } from "../../../SegUI/SegInput/SegInput";
import {
  SegPatenteInput,
  normalizarPatente,
} from "../../../SegUI/SegPatenteInput/SegPatenteInput";
import { SegButton } from "../../../SegUI/SegButton/SegButton";
import { SegSlider } from "../../../SegUI/SegSlider/SegSlider";
import { imagesCompanias, imagenesCompaniasFiltradas } from "../../../../Assets/imageCompania";
import { IPasos } from "../../../../Pages/Auto/Cotizador";
import { obtenerPatente } from "../../../../Services/API/bff/auto";
import { useNavigate } from "react-router-dom";
import { useCompania } from "../../../../Routers/AppRouter/cotizadorDeCompania";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import {
  TelefonosCompañia,
  telefonos,
} from "../../../../Assets/telefonosCompanias";
import { useCotizacionStore } from "../../../../Services/Providers/CotizacionStore";
import { EmpresaProps } from "../../../../Services/Providers/useConfiguracionEmpresa";
import { useConfig } from "../../../../context/ConfigContext";
import { getRecaptchaKey } from "../../../../Services/API/helper";
import { getPasoDatos } from "../../../../utils/getRubro";
import { RX_PATENTE } from "../../Comunes/Cotizacion/validaciones";
import { useTextosCotizadorAuto } from "../../../../Services/ventaPresencial/textosAsesor";

type FormValues = {
  setPatente: string;
};

interface InicioNormalProps {
  config: EmpresaProps;
  setSteps: Dispatch<SetStateAction<IPasos>>;
}

export const InicioNormal = ({ config, setSteps, ...rest }: InicioNormalProps) => {
  const { config: config_origen } = useConfig();
  const compania = useCompania();
  const nav = useNavigate();
  const { cotizacionValues, setCotizacionValues } = useCotizacionStore();
  const textos = useTextosCotizadorAuto("auto");

  const esArgentina = config_origen.pais === "AR";

  const [loading, setLoading] = useState(false);
  const [busquedaLanzada, setBusquedaLanzada] = useState(false);
  const {
    handleSubmit,
    control,
    setValue,
    formState,
    trigger,
    setError,
  } = useForm<FormValues>();

  // Lo que el paso de precios devuelve por query string.
  const urlParams = new URLSearchParams(window.location.search);
  const error = urlParams.get("error");

  const adaptativeMargin = `${4 / (window?.devicePixelRatio || 1)}vh`

  /**
   * El paso siguiente es siempre el mismo: la pantalla de datos. Cambia cómo
   * llega el vehículo — resuelto por el datacar, o para cargar a mano si la
   * patente no trajo nada.
   */
  const onSubmit: SubmitHandler<FormValues> = async(values: FormValues) => {
    const { setPatente } = values;
    setLoading(true);
    const key = await getRecaptchaKey();
    grecaptcha.ready(function () {
      grecaptcha.execute(key!, { action: "datacar" }).then(function (token) {
        const fetchData = async () => {
          const response: any = await obtenerPatente(values.setPatente, token);
          if (response != null) {
            // El BFF resuelve los ids del catálogo por nombre y puede no
            // encontrarlos. Sin ids no se puede encadenar modelos ni versiones:
            // el paso de datos pasa a carga manual con los nombres precargados.
            const sinIds = response.idsResueltos === false;
            setCotizacionValues({
              ...cotizacionValues,
              marca: { label: response.marca, value: response.marca },
              modelo: { label: response.modelo, value: response.modelo },
              version: { label: response.version, value: response.version },
              año: { label: response.anio, value: response.anio },
              esAntiguo: response.esAntiguo,
              patente: setPatente,
              vehiculoSinIds: sinIds,
            });
            nav(getPasoDatos("auto", compania));
          } else {
            // Sin datos del datacar se carga a mano: no puede quedar un
            // vehículo viejo en el store haciendo pasar el paso por resuelto.
            setCotizacionValues({
              ...cotizacionValues,
              marca: undefined,
              modelo: undefined,
              version: undefined,
              año: undefined,
              esAntiguo: undefined,
              patente: undefined,
              vehiculoSinIds: undefined,
            });
            // Se avisa en el paso de datos que la patente no trajo nada (va por
            // state, no por store, para no reactivar el modo "patente").
            nav(getPasoDatos("auto", compania), {
              state: { patenteSinDatos: true },
            });
          }
        };
        fetchData().then(() => {
          setLoading(false);
        });
      });
    });
  };

  /**
   * Cuando se llega con ?buscar=1 y una patente válida en el store, la búsqueda
   * arranca sola. Lo usa la card del hero de la home: la persona ya escribió la
   * patente y apretó "Cotizá", no tiene sentido pedirle que apriete de nuevo.
   * Corre una sola vez y no si el datacar está apagado o si hay un error dando
   * vueltas en la URL.
   */
  useEffect(() => {
    if (busquedaLanzada) return;
    if (urlParams.get("buscar") !== "1" || error) return;
    if (cotizacionValues?.parametrosIniciales?.dataCarHabilitado === false) return;
    const guardada = normalizarPatente(cotizacionValues?.patente || "");
    if (!RX_PATENTE.test(guardada)) return;
    setBusquedaLanzada(true);
    onSubmit({ setPatente: guardada });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    busquedaLanzada,
    cotizacionValues?.patente,
    cotizacionValues?.parametrosIniciales?.dataCarHabilitado,
  ]);

  /**
   * Cotizar sin patente: se carga a mano. NO se borra el vehículo: si ya había
   * uno cargado (p. ej. se volvió a la home y se retoma la cotización), se
   * conserva y reaparece en el paso de datos. Solo se limpia la patente.
   */
  const cotizarSinPatente = () => {
    setCotizacionValues({
      ...cotizacionValues,
      patente: undefined,
    });
    nav(getPasoDatos("auto", compania));
  };

  const tel = telefonos.filter((t: TelefonosCompañia) => {
    if (t.clave === compania) {
      return t;
    }
  });

  useEffect(() => {
    // Sin datacar no hay paso de patente: se va directo a cargar todo a mano.
    if (cotizacionValues?.parametrosIniciales?.dataCarHabilitado === false) {
      nav(getPasoDatos("auto", compania), { replace: true });
    }
  }, [compania, cotizacionValues.parametrosIniciales?.dataCarHabilitado]);

  /**
   * En Argentina se normaliza, porque en la sesión puede haber quedado una
   * patente guardada antes de que el campo tuviera forma de chapa. Donde el
   * campo sigue siendo un input común, se respeta lo que se había escrito.
   */
  const patenteGuardada = () => {
    if (!cotizacionValues?.patente) return "";
    return esArgentina
      ? normalizarPatente(cotizacionValues.patente)
      : cotizacionValues.patente;
  };

  useEffect(() => {
    setValue("setPatente", patenteGuardada());
    cotizacionValues?.patente && trigger("setPatente");
  }, [cotizacionValues?.patente]);

  useEffect(() => {
    if (error) {
      setError("setPatente", {
        type: "custom",
        message: error,
      });
    } else {
      setValue("setPatente", patenteGuardada());
      cotizacionValues?.patente && trigger();
    }
  }, []);


  return (
    <div className="text-center mt-3" {...rest}>
      <div className="bg-gray-light text-center sec-patente">
        <div className="container">
          <form className="frm01" onSubmit={handleSubmit(onSubmit)}>
            <fieldset>
              <SegP
                text={textos.patente}
                fontSize={22}
                backgroundColor="#F8F8F8"
                fontWeight={"bold"}
                margin="0 0 18px 0"
              />
              <SegP
                text="Con la patente traemos marca, modelo y versión. Sin espacios ni guiones."
                fontSize={15}
                backgroundColor="#F8F8F8"
                fontWeight={500}
                margin="-4px 0 14px"
                lineHeight={1.25}
              />
              <div className="mw-sm-450 m-auto">
                <Controller
                  name="setPatente"
                  control={control}
                  rules={{
                    validate: {
                      required: (value) => !!value || "Ingresá la patente",
                      pattern: (value) =>
                        RX_PATENTE.test(value) || "Patente inválida",
                    },
                  }}
                  render={({ field, fieldState }) =>
                    /* La chapa es el formato de patente argentino. En el resto
                       de los países el campo queda como estaba. */
                    esArgentina ? (
                      <SegPatenteInput
                        value={field.value || ""}
                        onChange={(patente) => {
                          field.onChange(patente);
                          setValue("setPatente", patente);
                          trigger("setPatente");
                        }}
                        leyendaError={fieldState?.error?.message}
                        autoFocus={true}
                        focusColor={
                          config.origen !== "SEGURARSE"
                            ? config.colors.accent
                            : "#2c7dfd"
                        }
                      />
                    ) : (
                      <SegInput
                        placeHolder="Ej: FUH384"
                        name="patente"
                        type="text"
                        width={100}
                        onChange={(e) => {
                          field.onChange(e);
                          setValue("setPatente", e.target.value);
                          trigger("setPatente");
                        }}
                        label=""
                        value={field.value}
                        leyendaError={fieldState?.error?.message}
                        autoFocus={true}
                        borderColor={`${formState.errors.setPatente
                          ? "var(--bs-form-invalid-border-color)"
                          : config.origen !== "SEGURARSE" ? config.colors.accent : ""
                          }`}
                        focusBorderColor={`${formState.errors.setPatente
                          ? "var(--bs-form-invalid-border-color)"
                          : config.origen !== "SEGURARSE" ? config.colors.accent : "#2c7dfd"
                          }`}
                      />
                    )
                  }
                />
              </div>
              <div className="form-buttons" style={{ marginTop: 2 }}>
                <SegButton
                  label="Buscar auto"
                  type="submit"
                  disabled={
                    !formState.isValid // Verifica si el formulario es válido
                  }
                  spinnerSize='small'
                  loading={loading}
                  bgColor={config.origen !== "SEGURARSE" ? config.colors.primary : undefined}
                  hoverBgColor={config.origen !== "SEGURARSE" ? config.colors.primary : undefined}
                  hoverBorderColor={config.origen !== "SEGURARSE" ? config.colors.accent : undefined}
                  hoverOpacity={config.origen !== "SEGURARSE"}
                />
              </div>
              <div className="form-alt">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none p-0"
                  style={{ color: "rgb(52, 52, 52)", fontWeight: 500, fontSize: 15 }}
                  onClick={cotizarSinPatente}
                >
                  Cotizar sin patente &gt;
                </button>
              </div>
              {compania && tel.length > 0 && (
                <p className="fs-12 mb-0 mt-2 text-dark">
                  ¿Buscás <b>{tel[0]?.txtMostrar}</b>? <b>Compará</b> las
                  mejores coberturas de las aseguradores líderes.
                </p>
              )}
            </fieldset>
          </form>
        </div>
        <div className="slide-cias" style={{ marginTop: adaptativeMargin }}>
          <SegSlider

            companias={
              config.origen !== "SEGURARSE" ?
                imagenesCompaniasFiltradas(cotizacionValues.parametrosIniciales.companias)
                :
                imagesCompanias.filter( comp => comp.pais?.includes(config_origen.pais))
            }

          />
        </div>
      </div>
    </div>
  );
};
